<template>
  <div class="h-full">
    <Component
      class="py-4"
      :is="!query || filteredTags.length > 0 ? SelectTags : NoTags"
      :tags="filteredTags"
      :activeTagIds="activeTagIds"
      :query="query"
      @createNewTag="handleCreateNewTag"
      @toggleTagSelected="handleToggleTagSelected"
    />
  </div>
</template>

<script setup>
import axios from "axios";
import route from "ziggy-js";
import { isEqual, sortBy } from "lodash";
import { computed, inject, ref, shallowRef, watch, watchEffect } from "vue";
import { SelectTags, NoTags } from "@/Components/Tags";
import { useTags } from "@/Composables/useTags";
import { Plus } from "@/Icons";

const emit = defineEmits(["update:initialState", "apply", "isLoaded"]);

const props = defineProps({
  initialState: { type: Array, default: () => [] },
  query: { type: String, default: "" },
  siteId: { type: String, required: false },
  allowCreate: { type: Boolean, default: false },
  class: { type: null, required: false },
});

const internalState = ref(props.initialState);

const disableApply = computed(() => {
  return isEqual(sortBy(props.initialState), sortBy(internalState.value));
});

const {
  isLoading,
  data,
  refetch: refetchTags,
} = useTags(props.siteId ? [props.siteId] : []);

const tags = computed(() => {
  if (isLoading.value) {
    return [];
  }
  return data.value;
});

const parsedQuery = computed(() => props.query.trim().toLowerCase());

const activeTagIds = computed(() => {
  if (isLoading.value) {
    return [];
  }
  return tags.value
    .filter((tag) => internalState.value.includes(tag.id))
    .map((tag) => tag.id);
});

const newTag = shallowRef({
  id: "new_tag",
  icon: Plus,
  name: props.query,
});

watchEffect(() => {
  newTag.value.name = parsedQuery.value;

  if (!isLoading.value) {
    emit("isLoaded");
  }
});

const { hasPermission, permissions } = inject("permissions");
const canCreateTags = computed(
  () => props.allowCreate && hasPermission(permissions.createTag)
);

const newTagAlreadyExists = computed(() => {
  return tags.value.some(
    (tag) => tag.name.trim().toLowerCase() === parsedQuery.value
  );
});

const filteredTags = computed(() => {
  if (!parsedQuery.value) {
    return tags.value;
  }
  const filteredTags = Object.values(tags.value).filter((tag) => {
    return tag.name.trim().toLowerCase().includes(parsedQuery.value);
  });
  if (canCreateTags.value && !newTagAlreadyExists.value) {
    return [newTag.value, ...filteredTags];
  }
  return filteredTags;
});

function handleCreateNewTag() {
  axios
    .post(route("api.tags.create"), {
      name: newTag.value.name,
      tenant_id: props.siteId,
    })
    .then(() => {
      refetchTags.value().then((res) => {
        internalState.value = [
          ...internalState.value,
          res.data.find((tag) => tag.name === newTag.value.name).id,
        ];
      });
    });
}

function handleToggleTagSelected(tagId) {
  if (internalState.value.includes(tagId)) {
    internalState.value = internalState.value.filter((id) => id !== tagId);
    return;
  }
  internalState.value = [...internalState.value, tagId];
}

watch(
  () => internalState.value,
  () => {
    emit("update:initialState", internalState.value);
  }
);

function handleApply() {
  emit("apply", internalState.value);
}

defineExpose({
  showBack: false,
  showApply: true,
  showSearch: true,
  disableApply,
  handleBack: () => {},
  handleApply,
});
</script>
